import React, { useState } from "react";
import {
  IResourceItem,
  useGo,
  useIsExistAuthentication,
  useLogout,
  useResourceParams,
  useTranslate,
} from "@refinedev/core";
import {
  ActionIcon,
  Box,
  Drawer,
  Navbar,
  NavLink,
  NavLinkStylesNames,
  NavLinkStylesParams,
  ScrollArea,
  MediaQuery,
  Button,
  Tooltip,
  TooltipProps,
  Styles,
} from "@mantine/core";
import {
  IconList,
  IconMenu2,
  IconChevronRight,
  IconChevronLeft,
  IconLogout,
  IconDashboard,
} from "@tabler/icons";
import { useLocation } from "react-router-dom";

import { Title as DefaultTitle } from "../title";

const defaultNavIcon = <IconList size={18} />;
type ResourceNode = IResourceItem & { children: ResourceNode[] };

export const Sider: React.FC<{ render?: any }> = ({ render }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [opened, setOpened] = useState(false);

  const { resources } = useResourceParams();
  const { pathname } = useLocation();
  const isExistAuthentication = useIsExistAuthentication();
  const t = useTranslate();
  const go = useGo();
  const { mutate: mutateLogout } = useLogout();

  const RenderToTitle = DefaultTitle;

  const drawerWidth = () => {
    if (collapsed) return 80;
    return 200;
  };

  const commonNavLinkStyles: Styles<NavLinkStylesNames, NavLinkStylesParams> = {
    root: {
      display: "flex",
      color: "white",
      fontWeight: 500,
      borderRadius: 14,
      marginBottom: 6,
      paddingTop: 6,
      paddingBottom: 6,
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.08)",
      },
      "&[data-active]": {
        background:
          "linear-gradient(135deg, rgba(99, 102, 241, 0.92) 0%, rgba(168, 85, 247, 0.92) 100%)",
        color: "white",
        fontWeight: 700,
        boxShadow: "0 12px 24px rgba(15, 23, 42, 0.24)",
        "&:hover": {
          background:
            "linear-gradient(135deg, rgba(99, 102, 241, 0.92) 0%, rgba(168, 85, 247, 0.92) 100%)",
        },
      },
      justifyContent: collapsed && !opened ? "center" : "flex-start",
    },
    icon: {
      marginRight: collapsed && !opened ? 0 : 12,
    },
    body: {
      display: collapsed && !opened ? "none" : "flex",
    },
  };

  const commonTooltipProps: Partial<TooltipProps> = {
    disabled: !collapsed || opened,
    position: "right",
    withinPortal: true,
    withArrow: true,
    arrowSize: 8,
    arrowOffset: 12,
    offset: 4,
  };

  const buildTree = (parent?: string): ResourceNode[] => {
    return resources
      .filter((resource: IResourceItem) => {
        const resourceParent = resource.meta?.parent;
        const hidden = resource.meta?.hide;
        return !hidden && resourceParent === parent;
      })
      .map((resource: IResourceItem) => ({
        ...resource,
        children: buildTree(resource.name),
      }))
      .filter((resource) => resource.list || resource.children.length > 0);
  };

  const menuItems = buildTree();
  const selectedKey = pathname;
  const defaultOpenKeys = menuItems
    .flatMap((item) => [item, ...item.children])
    .filter(
      (item: ResourceNode) =>
        item.children.length > 0 &&
        item.children.some((child: ResourceNode) =>
          pathname.startsWith(String(child.list ?? "")),
        ),
    )
    .map((item: ResourceNode) => String(item.list ?? item.name));

  const renderTreeView = (tree: typeof menuItems, selectedKey: string) => {
    return tree.map((item: ResourceNode) => {
      const route = typeof item.list === "string" ? item.list : undefined;
      const icon = item.meta?.icon;
      const { name, children } = item;
      const itemKey = route || name;
      const itemLabel = item.meta?.label ?? name;

      const isSelected = route === selectedKey || (!!route && selectedKey.startsWith(`${route}/`));
      const isParent = children.length > 0;

      return (
        <Tooltip key={itemKey} label={itemLabel} {...commonTooltipProps}>
          <NavLink
            label={collapsed && !opened ? null : itemLabel}
            icon={icon ?? defaultNavIcon}
            active={isSelected}
            childrenOffset={collapsed && !opened ? 0 : 12}
            defaultOpened={defaultOpenKeys.includes(String(route || name))}
            styles={commonNavLinkStyles}
            onClick={isParent || !route ? undefined : () => go({ to: route, type: "push" })}
          >
            {isParent && renderTreeView(children, selectedKey)}
          </NavLink>
        </Tooltip>
      );
    });
  };

  const items = renderTreeView(menuItems, selectedKey);

  const dashboard = false ? (
    <Tooltip
      label={t("dashboard.title", "Dashboard")}
      {...commonTooltipProps}
    >
      <NavLink
        key="dashboard"
        label={collapsed && !opened ? null : t("dashboard.title", "Dashboard")}
        icon={<IconDashboard size={18} />}
        onClick={() => go({ to: "/", type: "push" })}
        active={selectedKey === "/"}
        styles={commonNavLinkStyles}
      />
    </Tooltip>
  ) : null;

  const logout = isExistAuthentication && (
    <Tooltip label={t("buttons.logout", "Logout")} {...commonTooltipProps}>
      <NavLink
        key="logout"
        label={collapsed && !opened ? null : t("buttons.logout", "Logout")}
        icon={<IconLogout size={18} />}
        onClick={() => mutateLogout()}
        styles={commonNavLinkStyles}
      />
    </Tooltip>
  );

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        logout,
        items,
        collapsed,
      });
    }
    return (
      <>
        {dashboard}
        {items}
        {logout}
      </>
    );
  };

  return (
    <>
      <MediaQuery largerThan="md" styles={{ display: "none" }}>
        <Box sx={{ position: "fixed", top: 64, left: 0, zIndex: 1199 }}>
          <ActionIcon
            color="white"
            size={36}
            sx={{
              borderRadius: "0 12px 12px 0",
              background:
                "linear-gradient(180deg, rgba(79, 70, 229, 1) 0%, rgba(124, 58, 237, 1) 100%)",
              color: "white",
              boxShadow: "0 16px 30px rgba(15, 23, 42, 0.28)",
              "&:hover": {
                background:
                  "linear-gradient(180deg, rgba(79, 70, 229, 1) 0%, rgba(124, 58, 237, 1) 100%)",
              },
            }}
            onClick={() => setOpened((prev) => !prev)}
          >
            <IconMenu2 />
          </ActionIcon>
        </Box>
      </MediaQuery>

      <MediaQuery largerThan="md" styles={{ display: "none" }}>
        <Drawer
          opened={opened}
          onClose={() => setOpened(false)}
          size={200}
          zIndex={1200}
          withCloseButton={false}
          styles={{
            drawer: {
              overflow: "hidden",
              background:
                "linear-gradient(180deg, #0d1528 0%, #10182d 38%, #0a1222 100%)",
            },
          }}
        >
          <Navbar.Section px="xs">
            <RenderToTitle collapsed={false} />
          </Navbar.Section>
          <Navbar.Section grow component={ScrollArea} mx="-xs" px="xs">
            {renderSider()}
          </Navbar.Section>
        </Drawer>
      </MediaQuery>

      <MediaQuery smallerThan="md" styles={{ display: "none" }}>
        <Box
          sx={{
            width: drawerWidth(),
            transition: "width 200ms ease, min-width 200ms ease",
          }}
        />
      </MediaQuery>

      <MediaQuery smallerThan="md" styles={{ display: "none" }}>
        <Navbar
          width={{ base: drawerWidth() }}
          sx={{
            overflow: "hidden",
            transition: "width 200ms ease, min-width 200ms ease",
            background:
              "linear-gradient(180deg, #0d1528 0%, #10182d 38%, #0a1222 100%)",
            position: "fixed",
            top: 0,
            height: "100vh",
            borderRight: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Navbar.Section px="xs">
            <RenderToTitle collapsed={collapsed} />
          </Navbar.Section>
          <Navbar.Section grow mt="sm" component={ScrollArea} mx="-xs" px="xs">
            {renderSider()}
          </Navbar.Section>
          <Navbar.Section>
            <Button
              sx={{
                background: "rgba(255,255,255,0.06)",
                borderRadius: 0,
                borderTop: "1px solid rgba(255,255,255,0.08)",
              }}
              size="md"
              variant="subtle"
              color="gray"
              fullWidth
              onClick={() => setCollapsed((prev) => !prev)}
            >
              {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
            </Button>
          </Navbar.Section>
        </Navbar>
      </MediaQuery>
    </>
  );
};
